// @flow strict

import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { PictureOutlined, DeleteOutlined } from "@ant-design/icons";
// avatarBlank

// Memoized image item component for better performance
const ImageItem = React.memo(({ url, index, onRemove }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isBlobUrl = url && url.startsWith('blob:');
  
  // Reset loading state when URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [url]);

  return (
    <div style={{ position: "relative", display: "inline-block", paddingTop: "8px", paddingRight: "8px" }}>
      {!imageLoaded && !imageError && !isBlobUrl && (
        <div 
          style={{ 
            width: "100px", 
            height: "100px", 
            background: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "15px",
            position: "absolute",
            zIndex: 1
          }}
        >
          <PictureOutlined style={{ fontSize: 24, color: "#999" }} />
        </div>
      )}
      <img 
        src={url} 
        className="product-image-widget-image" 
        style={{ 
          width: "100px", 
          height: "100px", 
          objectFit: "cover",
          display: "block"
        }}
        alt={`Preview ${index + 1}`}
        loading={isBlobUrl ? "eager" : "lazy"}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          setImageError(true);
          setImageLoaded(false);
        }}
      />
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(index);
        }}
        className="image-delete-button"
        style={{
          position: "absolute",
          top: "0px",
          right: "0px",
          background: "red",
          color: "white",
          border: "2px solid white",
          borderRadius: "50%",
          width: "24px",
          height: "24px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
          padding: 0,
          outline: "none",
        }}
      >
        <DeleteOutlined />
      </button>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if url or index changes
  return prevProps.url === nextProps.url && prevProps.index === nextProps.index;
});

ImageItem.displayName = 'ImageItem';

function ProductImageWidget(props) {
  const [imageUrls, setImageUrls] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]); // Track existing URLs separately
  const [newFileUrls, setNewFileUrls] = useState([]); // Track new file blob URLs separately
  const initialThumbnailRef = useRef(null); // Track initial thumbnail_image value
  const isInternalUpdateRef = useRef(false); // Flag to track internal updates
  const blobUrlsRef = useRef([]); // Track all blob URLs for cleanup
  const { productInfo, setProductInfo } = props;
  const { errorField, setErrorField } = props;
  const { id, field } = props;

  // Memoize parsing logic to avoid re-parsing on every render
  const parseThumbnailImage = useCallback((thumbnail) => {
    if (!thumbnail || thumbnail === "") return [];
    
    let images = thumbnail;
    
    // Try to parse if it's a JSON string
    if (typeof images === 'string') {
      try {
        const parsed = JSON.parse(images);
        if (Array.isArray(parsed)) {
          images = parsed;
        }
      } catch (e) {
        // Not JSON, treat as single image URL string
      }
    }
    
    // Convert to array
    if (Array.isArray(images)) {
      return images;
    } else if (images) {
      return [images];
    }
    return [];
  }, []);

  // Optimized comparison - only compare if values actually changed
  const currentThumbnail = productInfo.thumbnail_image;
  const thumbnailString = useMemo(() => {
    return currentThumbnail ? JSON.stringify(currentThumbnail) : "";
  }, [currentThumbnail]);

  useEffect(() => {
    // Skip if this is an internal update (we're updating thumbnail_image ourselves)
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }
    
    // Check if thumbnail_image has changed from outside (new record loaded)
    const initialString = initialThumbnailRef.current ? JSON.stringify(initialThumbnailRef.current) : "";
    const thumbnailChanged = initialString !== thumbnailString;
    
    if (thumbnailChanged || initialThumbnailRef.current === null) {
      // Cleanup old blob URLs before resetting
      blobUrlsRef.current.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      blobUrlsRef.current = [];
      
      const imageArray = parseThumbnailImage(currentThumbnail);
      setImageUrls(imageArray);
      setExistingImageUrls(imageArray); // Store existing URLs
      setNewFileUrls([]); // Reset new files
      initialThumbnailRef.current = currentThumbnail; // Update ref
    }
  }, [thumbnailString, currentThumbnail, parseThumbnailImage]);
  
  // Update imageUrls whenever existingImageUrls or newFileUrls change
  useEffect(() => {
    const combined = [...existingImageUrls, ...newFileUrls];
    setImageUrls(combined);
  }, [existingImageUrls, newFileUrls]);

  // Cleanup blob URLs on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup all blob URLs when component unmounts
      blobUrlsRef.current.forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      blobUrlsRef.current = [];
    };
  }, []);

  const onDropImage = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Create preview URLs for all selected files (batch update for better performance)
    const newUrls = files.map(file => URL.createObjectURL(file));
    blobUrlsRef.current = [...blobUrlsRef.current, ...newUrls]; // Track for cleanup
    
    // Update new file URLs - this will trigger imageUrls update via useEffect
    setNewFileUrls(prev => {
      const updated = [...prev, ...newUrls];
      return updated;
    });
    
    // Store files array
    const existingFiles = productInfo.image_name ? 
      (Array.isArray(productInfo.image_name) ? productInfo.image_name : [productInfo.image_name]) 
      : [];
    
    // Update thumbnail_image to include existing URLs + new files will be uploaded
    // For now, keep existing thumbnail_image and add new files to image_name
    // The backend will handle merging them
    setProductInfo((prev) => ({ 
      ...prev, 
      image_name: [...existingFiles, ...files],
      // Keep existing thumbnail_image for display, new files will be added after upload
    }));
    
    // Reset file input to allow selecting same files again
    e.target.value = '';
  }, [productInfo.image_name, setProductInfo]);

  const removeImage = useCallback((index) => {
    const removedUrl = imageUrls[index];
    if (!removedUrl) return;
    
    // Check if removed image is an existing URL (starts with http) or a new file (blob URL)
    const isExistingUrl = removedUrl.startsWith('http://') || removedUrl.startsWith('https://');
    
    if (isExistingUrl) {
      // Remove from existing URLs
      setExistingImageUrls(prev => {
        const updated = prev.filter(url => url !== removedUrl);
        
        // Update thumbnail_image
        let updatedThumbnail = updated;
        if (updated.length === 1) {
          updatedThumbnail = updated[0];
        } else if (updated.length === 0) {
          updatedThumbnail = "";
        }
        
        // Update thumbnail_image but don't trigger full reset
        // Set flag to indicate this is an internal update
        isInternalUpdateRef.current = true;
        initialThumbnailRef.current = updatedThumbnail;
        setProductInfo(prev => ({ 
          ...prev, 
          thumbnail_image: updatedThumbnail
        }));
        
        return updated;
      });
    } else {
      // Remove from new file uploads (blob URLs)
      // Find the index in newFileUrls array
      const blobIndex = newFileUrls.findIndex(url => url === removedUrl);
      if (blobIndex >= 0) {
        setNewFileUrls(prev => prev.filter((_, i) => i !== blobIndex));
        
        // Also remove from image_name files array and revoke blob URL
        setProductInfo(prev => {
          const existingFiles = prev.image_name ? 
            (Array.isArray(prev.image_name) ? prev.image_name : [prev.image_name]) 
            : [];
          
          if (blobIndex < existingFiles.length) {
            // Revoke blob URL to free memory
            URL.revokeObjectURL(removedUrl);
            blobUrlsRef.current = blobUrlsRef.current.filter(url => url !== removedUrl);
            return {
              ...prev,
              image_name: existingFiles.filter((_, i) => i !== blobIndex)
            };
          }
          return prev;
        });
      }
    }
  }, [imageUrls, newFileUrls, setProductInfo]);

  return (
    <div className="product-image-widget">
      <label htmlFor={id === undefined ? "upload_product_image" : id}>
        <input
          type="file"
          id={id === undefined ? "upload_product_image" : id}
          name={id === undefined ? "upload_product_image" : id}
          style={{ display: "none" }}
          accept="image/x-png,image/gif,image/jpeg"
          multiple
          onChange={onDropImage}
        />
        {imageUrls.length === 0 ? (
          <img
            className={
              "product-image-widget-image " +
              (errorField.includes("image_name") ? "product-image-widget-error" : "")
            }
            src={'/assets/icons/ic_picture.svg'}
            alt="Kalaecafe"
          />
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", justifyContent: "center", maxHeight: "400px", overflowY: "auto" }}>
            {imageUrls.map((url, index) => (
              <ImageItem
                key={`${url}-${index}`}
                url={url}
                index={index}
                onRemove={removeImage}
              />
            ))}
          </div>
        )}
        <div style={{ textAlign: "center", cursor: "pointer", marginTop: "10px", marginBottom: "10px" }}>
          {imageUrls.length === 0 ? "click here to insert images" : "click here to add more images"}
        </div>
      </label>
    </div>
  );
}

export default ProductImageWidget;
